// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import { ClaudeMark } from '@/components/knowledge/ClaudeMark';
import { ScopeTile } from '@/components/knowledge/ScopeTile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export interface ProposalRowProps {
  title: string;
  scopeLabel: string;
  isNewEntry: boolean;
  isAgent: boolean;
  proposerLabel: string;
  relativeTime: string;
  rationale: string;
  tile: {
    scope: 'org' | 'integration' | 'app';
    integrationSlug?: string;
    integrationName?: string;
    appName?: string;
    appSlug?: string;
  };
  entityLink?: { to: string; label: string; testId: string };
  testId: string;
  onReview: () => void;
}

export function ProposalRow({
  title,
  scopeLabel,
  isNewEntry,
  isAgent,
  proposerLabel,
  relativeTime,
  rationale,
  tile,
  entityLink,
  testId,
  onReview,
}: ProposalRowProps): React.JSX.Element {
  return (
    <li
      className="flex flex-col gap-3 px-4 md:px-5 py-4 md:flex-row md:items-center"
      data-testid={testId}
    >
      {/* Left: scope icon tile */}
      <ScopeTile {...tile} size={38} />

      {/* Middle: title, attribution, rationale */}
      <div className="min-w-0 flex-1 space-y-1">
        {/* Line 1: title + scope badge + new entry badge */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13.5px] font-semibold">{title}</span>
          <Badge variant="outline">{scopeLabel}</Badge>
          {isNewEntry && (
            <Badge variant="secondary" className="whitespace-nowrap">
              New entry
            </Badge>
          )}
        </div>

        {/* Line 2: attribution chip */}
        <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          {isAgent && <ClaudeMark size={13} />}
          <span>{proposerLabel}</span>
          <span>
            · {isNewEntry ? 'proposed new entry' : 'proposed edit'} · {relativeTime}
          </span>
        </div>

        {/* Line 3: rationale */}
        <p className="text-[12px] text-muted-foreground truncate">{rationale}</p>
      </div>

      {/* Right: action buttons */}
      <div className="flex flex-wrap items-center gap-2 md:shrink-0">
        {entityLink && (
          <Button variant="ghost" size="sm" asChild>
            <Link to={entityLink.to} data-testid={entityLink.testId}>
              {entityLink.label}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onReview}>
          Review
        </Button>
      </div>
    </li>
  );
}
